/**
 * Cookie-backed Storage adapter for Supabase Auth.
 *
 * Why this exists
 * ---------------
 * Supabase Auth's default storage is `window.localStorage`, which is
 * scoped per-origin. For www.jubit.ai and play.jubit.ai (the jubuddy
 * surface) to share a session, we need the session to live in a cookie
 * with `Domain=.jubit.ai` so both subdomains read the same value.
 *
 * Implementation notes
 * --------------------
 * Supabase only requires the `getItem` / `setItem` / `removeItem` triplet
 * (the full Storage interface defines `clear`, `key`, `length` too — we
 * implement those for completeness so downstream typings don't complain).
 *
 * Cookies cap at ~4 KB per cookie and ~80 KB per domain. A signed-in
 * Supabase session with a refresh token + user object can hit 3–5 KB —
 * over the per-cookie ceiling. We chunk the value into N cookies named
 * `${key}.0`, `${key}.1`, ... and read them back in order. This is the
 * same pattern @supabase/ssr uses server-side.
 *
 * Security
 * --------
 *   SameSite=Lax  — required for OAuth redirects to carry the cookie.
 *   Secure        — only sent over HTTPS (always, except localhost).
 *   HttpOnly: NO  — Supabase JS reads document.cookie client-side.
 *   Domain set explicitly so .jubit.ai sharing works.
 *   Path: /
 *
 * The cookie can't be HttpOnly because the SPA reads tokens via
 * `localStorage.getItem` semantics. That's the same exposure profile as
 * the default localStorage adapter. If you need HttpOnly cookies you
 * have to move auth to a server-side flow (Next.js / Remix), which is
 * out of scope here.
 */
const CHUNK_SIZE = 3500;
/**
 * Build a Storage-shaped adapter that persists keys as one or more
 * cookies. Returns null if `document` isn't available (SSR / Node).
 */
export function createCookieStorage(options = {}) {
    const doc = options.document ?? (typeof document !== 'undefined' ? document : null);
    if (!doc)
        return null;
    const path = options.path ?? '/';
    const sameSite = options.sameSite ?? 'lax';
    const maxAge = options.maxAgeSeconds ?? 60 * 60 * 24 * 60; // 60 days
    const isLocalhost = typeof location !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(location.hostname);
    const secure = options.secure ?? !isLocalhost;
    const attrs = (() => {
        const parts = [`Path=${path}`];
        if (options.domain)
            parts.push(`Domain=${options.domain}`);
        parts.push(`SameSite=${sameSite[0].toUpperCase()}${sameSite.slice(1)}`);
        if (secure)
            parts.push('Secure');
        return parts.join('; ');
    })();
    function readChunk(name) {
        const raw = doc.cookie || '';
        if (!raw)
            return null;
        const prefix = `${encodeURIComponent(name)}=`;
        for (const piece of raw.split('; ')) {
            if (piece.startsWith(prefix)) {
                return decodeURIComponent(piece.slice(prefix.length));
            }
        }
        return null;
    }
    function writeChunk(name, value) {
        const cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${maxAge}; ${attrs}`;
        doc.cookie = cookie;
    }
    function expireChunk(name) {
        const cookie = `${encodeURIComponent(name)}=; Max-Age=0; ${attrs}`;
        doc.cookie = cookie;
    }
    function listChunkNames(key) {
        const raw = doc.cookie || '';
        if (!raw)
            return [];
        const out = [];
        const baseEncoded = encodeURIComponent(key);
        const re = new RegExp(`^${baseEncoded.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\.(\\d+)=`);
        for (const piece of raw.split('; ')) {
            const m = piece.match(re);
            if (m)
                out.push(`${key}.${m[1]}`);
        }
        return out.sort((a, b) => {
            const ai = Number(a.slice(key.length + 1));
            const bi = Number(b.slice(key.length + 1));
            return ai - bi;
        });
    }
    return {
        getItem(key) {
            // Single-chunk path (most common).
            const direct = readChunk(key);
            if (direct !== null)
                return direct;
            // Reassemble from chunked cookies.
            const chunkNames = listChunkNames(key);
            if (chunkNames.length === 0)
                return null;
            const chunks = chunkNames.map(n => readChunk(n) ?? '');
            return chunks.join('');
        },
        setItem(key, value) {
            // Always expire any prior chunked form so stale chunks don't merge
            // back in if the value shrinks.
            const oldChunkNames = listChunkNames(key);
            for (const n of oldChunkNames)
                expireChunk(n);
            if (value.length <= CHUNK_SIZE) {
                writeChunk(key, value);
                return;
            }
            // Expire the single-chunk variant since we're switching to multi.
            expireChunk(key);
            let i = 0;
            for (let offset = 0; offset < value.length; offset += CHUNK_SIZE, i += 1) {
                writeChunk(`${key}.${i}`, value.slice(offset, offset + CHUNK_SIZE));
            }
        },
        removeItem(key) {
            expireChunk(key);
            for (const n of listChunkNames(key))
                expireChunk(n);
        },
        // The full Storage interface — Supabase doesn't call these but TS
        // declares them on Storage so we implement no-op stubs.
        clear() {
            // Intentionally a no-op. The adapter is scoped to one Supabase
            // session; clearing would risk wiping unrelated cookies.
        },
        key(_index) {
            return null;
        },
        get length() {
            return 0;
        },
    };
}
